import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, X, ChevronRight } from "lucide-react";
import { Screen, ScrollArea, PrimaryButton, TextField } from "@/components/jk/ui";
import { Avatar, Chip, BottomNav, Pill, FullScreenLoader } from "@/components/jk/kit";
import { formatDate } from "@/lib/format";
import api from "@/lib/api";

const FILTERS = ["All", "Recent", "Repeat", "New"];

const ClientList = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/clients").then(({ data }) => setClients(data.clients || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let rows = [...clients];
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((c) => c.full_name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.mobile_number?.includes(q));
    if (filter === "Repeat") rows = rows.filter((c) => (c.total_consultations || 0) > 1);
    else if (filter === "New") rows = rows.filter((c) => (c.total_consultations || 0) <= 1);
    if (filter === "Recent") rows.sort((a, b) => (b.last_consult_date || "").localeCompare(a.last_consult_date || ""));
    else rows.sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));
    return rows;
  }, [clients, search, filter]);

  return (
    <Screen testid="client-list-screen">
      <header className="shrink-0 px-6 pb-3 pt-8">
        <div className="flex items-baseline justify-between">
          <h1 className="text-[26px] font-bold tracking-[-0.02em] text-[#111111]">Clients</h1>
          <span className="text-[13px] text-[#666666]" data-testid="client-count">{clients.length} total</span>
        </div>
        <div className="relative mt-4">
          <Search size={18} strokeWidth={1.75} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#aaaaaa]" />
          <input
            data-testid="client-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients"
            className="h-[46px] w-full rounded-[12px] border border-[#e5e5e5] bg-[#f9f9f9] pl-11 pr-4 text-[14px] text-[#111111] outline-none placeholder:text-[#aaaaaa] focus:border-[#111111] focus:bg-white"
          />
        </div>
        <div className="mt-3 flex gap-2">
          {FILTERS.map((f) => (
            <Chip key={f} selected={filter === f} onClick={() => setFilter(f)} testid={`filter-${f.toLowerCase()}`}>{f}</Chip>
          ))}
        </div>
      </header>

      <ScrollArea className="px-6 pt-2">
        {loading ? (
          <div className="pt-20"><FullScreenLoader /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center pt-20 text-center" data-testid="clients-empty">
            <p className="text-[15px] font-medium text-[#111111]">{clients.length === 0 ? "No clients yet" : "No matches"}</p>
            <p className="mt-1 text-[13px] text-[#666666]">{clients.length === 0 ? "Clients appear here after their first booking." : "Try a different search or filter."}</p>
          </div>
        ) : (
          <div className="space-y-2.5 pb-24" data-testid="client-rows">
            {filtered.map((c) => (
              <button key={c.id} onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-row-${c.id}`} className="flex w-full items-center gap-3 rounded-[14px] border border-[#e9e6e1] bg-white p-3.5 text-left active:scale-[0.99]">
                <Avatar name={c.full_name} size={44} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-semibold text-[#111111]">{c.full_name}</p>
                  <p className="truncate text-[12px] text-[#666666]">
                    {c.last_service ? `${c.last_service}` : "No consultations yet"}
                    {c.last_consult_date ? ` · ${formatDate(c.last_consult_date)}` : ""}
                  </p>
                </div>
                {(c.total_consultations || 0) > 0 && <Pill tone="neutral">{c.total_consultations}</Pill>}
                <ChevronRight size={18} className="text-[#cfccc6]" />
              </button>
            ))}
          </div>
        )}
      </ScrollArea>

      <button onClick={() => setShowAdd(true)} data-testid="add-client-fab" className="absolute bottom-20 right-5 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-[#111111] shadow-lg active:scale-95">
        <Plus size={26} strokeWidth={2} className="text-white" />
      </button>

      {showAdd && <AddClientModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}

      <BottomNav />
    </Screen>
  );
};

const AddClientModal = ({ onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim() || !email.trim()) { setError("Name and email are required."); return; }
    setSaving(true);
    try {
      await api.post("/clients", { full_name: name.trim(), mobile_number: mobile.trim(), email: email.trim().toLowerCase() });
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not add client.");
      setSaving(false);
    }
  };

  return (
    <div className="absolute inset-0 z-30 flex items-end bg-black/40" onClick={onClose} data-testid="add-client-modal">
      <div className="w-full rounded-t-[24px] bg-[var(--jk-paper)] p-6 pb-8" onClick={(e) => e.stopPropagation()}>
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-[20px] font-bold text-[#111111]">Add client</h2>
          <button onClick={onClose} className="text-[#666666]"><X size={22} /></button>
        </div>
        <div className="space-y-4">
          <TextField testid="add-client-name" label="Full name" placeholder="Client name" value={name} onChange={(e) => { setName(e.target.value); setError(""); }} />
          <TextField testid="add-client-mobile" label="Mobile" inputMode="tel" placeholder="98765 43210" value={mobile} onChange={(e) => setMobile(e.target.value)} />
          <TextField testid="add-client-email" label="Email" type="email" placeholder="client@email.com" value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} error={error} />
        </div>
        <div className="mt-6">
          <PrimaryButton testid="add-client-save" onClick={save} disabled={saving}>{saving ? "Adding…" : "Add client"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default ClientList;
